import { z } from 'zod';

// The contract between the API and the web app: for every endpoint, its method and path, what a request may carry
// (path parameters, query string, JSON body) and what the response looks like. Both sides are written against this
// one description:
//
//   server   validates each request against it before any handler runs (middleware/validate.ts) and registers
//            the route from the same path, so a handler only ever sees data of the stated shape;
//   client   builds the URL and body from it and checks each response against it (services/api.ts), so a field
//            the server renamed fails loudly in one place instead of being `undefined` somewhere in the UI.
//
// The server owns this folder. The client keeps an identical copy (client/src/contract); see ./README.md.

// --- limits (the client shows these as hints, so they live here rather than in each side) ----------------------

export const LIMITS = {
    title: 200,                      // boards, sections and tasks
    description: 1000,               // a task's description
    recurringOccurrences: 100,
    searchQuery: 100,
    searchResults: 50,
    notificationsShown: 50,
    notificationsPerRequest: 100,    // most ids one mark-as-read call may name
    notificationKeptDays: 90,
    invitationValidDays: 30,         // an invitation not answered by then is gone
    seriesShiftMinutes: 24 * 60,
    statsRangeDays: 366,
    archiveAfterDays: 365,
    kioskMessage: 280,
    kioskPhotoLinks: 20,             // photos added by link
    kioskPhotoLinkLength: 2000,
    kioskPhotoUploads: 10,           // photos uploaded to storage
    kioskPhotoBytes: 10 * 1024 * 1024,
} as const;

export const KIOSK_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const;
export type KioskPhotoType = typeof KIOSK_PHOTO_TYPES[number];

// --- building blocks ---------------------------------------------------------------------------------------------

export const id = z.string().min(1, 'an id is required');

export const title = (what: string) =>
    z.string().trim().min(1, `${what} can't be empty`).max(LIMITS.title, `${what} must be at most ${LIMITS.title} characters`);

export const description = z.string().trim().max(LIMITS.description, `The description must be at most ${LIMITS.description} characters`);

export const isTimeZone = (timeZone: string) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    } catch {
        return false;
    }
};

export const isoDate = z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'must be a valid date');

export const boardRole = z.enum(['owner', 'editor', 'viewer', 'assignee']);
export type BoardRole = z.infer<typeof boardRole>;

// The roles someone can be given; "owner" is only ever the board's creator.
export const memberRole = z.enum(['viewer', 'assignee', 'editor']);
export type MemberRole = z.infer<typeof memberRole>;

export const seriesScope = z.enum(['following', 'all']);
export type SeriesScope = z.infer<typeof seriesScope>;

// What the server sends with any error status.
export const errorResponse = z.object({
    message: z.string(),
    issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});

// --- describing an endpoint ------------------------------------------------------------------------------------

export type Method = 'GET' | 'POST' | 'PUT';

export interface EndpointSpec {
    method: Method;
    // Relative to /api. Path parameters are written `:name`.
    path: string;
    params?: z.ZodTypeAny;
    query?: z.ZodTypeAny;
    body?: z.ZodTypeAny;
    response: z.ZodTypeAny;
}

// Returns its argument unchanged; it exists so each endpoint keeps its exact schemas (and so its types).
export const endpoint = <E extends EndpointSpec>(spec: E): E => spec;

type Out<S> = S extends z.ZodTypeAny ? z.output<S> : never;
type In<S> = S extends z.ZodTypeAny ? z.input<S> : never;

// What a handler receives (after validation and defaults) and what a caller may send.
export type ParamsOf<E extends EndpointSpec> = Out<E['params']>;
export type QueryOf<E extends EndpointSpec> = Out<E['query']>;
export type BodyOf<E extends EndpointSpec> = Out<E['body']>;
export type ParamsInput<E extends EndpointSpec> = In<E['params']>;
export type BodyInput<E extends EndpointSpec> = In<E['body']>;
export type QueryInput<E extends EndpointSpec> = In<E['query']>;
// What a handler must send and what a caller receives.
export type ReplyOf<E extends EndpointSpec> = z.output<E['response']>;

// The path with its parameters filled in: pathFor('/boards/:boardId/tasks', { boardId: 'b1' }) -> '/boards/b1/tasks'.
export const pathFor = (path: string, params: Record<string, string> = {}): string =>
    path.replace(/:(\w+)/g, (_match, name: string) => {
        const value = params[name];
        if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
        return encodeURIComponent(value);
    });

export const boardParams = z.object({ boardId: id });
